import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Loader2, X, PackageSearch, ChevronLeft } from "lucide-react";
import StorefrontModal from "./StorefrontModal";
import VerificationModal from "./VerificationModal";
import { formatUsPhoneDisplay, isValidPhone } from "../../../lib/contactValidation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Post-deployment correction (Aug 2026) — same money-formatting shape
// `OrderDetailsDrawer.jsx` (admin) already uses, kept local rather than
// imported since this component intentionally has no dependency on any
// admin-only module.
function formatMoney(v, currency) {
  if (v == null || Number.isNaN(Number(v))) return "";
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[(currency || "usd").toUpperCase()] || "";
  return sym ? `${sym}${Number(v).toFixed(2)}` : `${Number(v).toFixed(2)} ${(currency || "").toUpperCase()}`.trim();
}

function TotalRow({ label, value, bold = false }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-stone-600">{label}</span>
      <span className={bold ? "font-bold text-stone-900" : "text-stone-800"}>{value}</span>
    </div>
  );
}

/**
 * PastOrdersLookup — returning-customer past-order history (initial
 * go-live completion pass, Aug 2026; UX correction, Aug 2026).
 *
 * Restores the previously-missing capability: a customer who closes
 * the Stripe success/return tab (or just comes back later) had no way
 * to reopen a past order. Runs on the SAME OTP verification primitives
 * every other sensitive storefront action uses (`VerificationModal`,
 * `purpose="order_lookup"` — forced REQUIRED server-side regardless of
 * the tenant's checkout OTP policy, see `routes/retail_otp.py`), then
 * calls `POST /api/public/retail/{slug}/orders` /
 * `POST /api/public/retail/{slug}/orders/{order_code}`
 * (`retail/customer_order_history.py`) to list/detail ONLY that
 * verified customer's own orders.
 *
 * UX correction (Aug 2026) — ONE verification grant now covers both
 * `WelcomeModal`'s recognized-customer banner AND this component: if
 * the parent already holds a fresh `verifiedProof` (the customer just
 * completed OTP to see their saved details), it's passed in via the
 * `verifiedProof` prop and reused directly — no second phone entry, no
 * second OTP. If it's absent, expired, or otherwise rejected by the
 * backend, this component transparently falls back to its own
 * phone-entry + OTP flow (never a silent failure). Never trusts its
 * own local "verified" state as anything more than a UI convenience —
 * the backend independently re-validates the proof's scope/freshness
 * on every list AND every detail call.
 *
 * UX correction (Aug 2026) — selecting an order no longer navigates
 * the browser to a standalone order-status page (which had no way
 * back to the in-progress Sales Order form). It now opens an
 * in-overlay "detail" view within this SAME modal — the underlying
 * Sales Order form is never unmounted, never reset, and is exactly as
 * the customer left it once this overlay closes.
 *
 * Post-deployment correction (Aug 2026) — the detail view previously
 * showed only order number / fulfillment mode / total, the exact same
 * fields already visible in the list, because the backend's detail
 * endpoint reused the list's summary-only shape. It now renders the
 * itemized breakdown (`retail/customer_order_history.py::
 * _safe_order_detail`): every line item at its authoritative stored
 * price, subtotal, discount, tax, tip, delivery fee, and refund status
 * — never recalculated client-side. The list itself is also now capped
 * at the five most recent orders, enforced server-side.
 *
 * Steps: enter_phone -> verifying (VerificationModal) -> loading ->
 *        list | detail | error
 */
export default function PastOrdersLookup({
  slug, catalogId, sessionId, verifiedProof, onLoyaltyProof, onClose,
  // Correct the Failed PR #140/#142 Walk-In Workflow (Sep 2026) —
  // confirmed staging defect: this component always started its own
  // phone field blank, even when the caller (WelcomeModal) already had
  // a complete, settled phone number the customer had just typed on
  // the Sales Order form moments earlier — forcing a pointless second
  // entry of the SAME number. `initialPhone` seeds the field only;
  // the customer can still edit it (e.g. to look up a different
  // number), and nothing here trusts it as verified — the OTP step
  // below still independently confirms it.
  initialPhone = "",
  // Correct the Failed PR #140/#142 Walk-In Workflow (Sep 2026) —
  // confirmed staging defect: even when the Sales Order form's safe
  // (non-OTP) pre-check already found active, on-file SMS consent for
  // this exact phone number, this component's own fallback OTP flow
  // always asked the customer to consent again, because it never told
  // `VerificationModal` a consent signal existed at all (`customer=
  // {{ phone }}` — no `smsConsent` field). `VerificationModal` itself
  // already reads `customer.smsConsent` as a caller-supplied hint —
  // exactly the same pattern `WelcomeModal`'s own OTP flow already
  // uses (`customer={{ phone, email, smsConsent }}`) — this just
  // propagates the SAME signal into this second caller, which had
  // simply never wired it through. The backend independently
  // re-derives/confirms durable consent-on-file regardless; this is a
  // UX convenience, never the enforcement boundary.
  initialSmsConsent = false,
  // Oct 2026 — true only when the catalog's tenant turned "Require OTP
  // verification" off. Forwarded to `VerificationModal`, which then asks
  // the server for a phone-access grant instead of sending a code. The
  // server re-checks the tenant setting; this is never the enforcement.
  phoneAccessAllowed = false,
}) {
  const hasReusableProof = !!(verifiedProof?.token && verifiedProof?.channel && verifiedProof?.destinationValue);
  const [step, setStep] = useState(hasReusableProof ? "loading" : "enter_phone");
  const [phone, setPhone] = useState(initialPhone || "");
  const [orders, setOrders] = useState([]);
  const [errorMsg, setErrorMsg] = useState("");
  const [expiredNotice, setExpiredNotice] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  // UX correction (Aug 2026) — the exact proof currently in effect,
  // whether reused from the parent or obtained by this component's
  // own fallback OTP flow. `fetchOrders` and `openOrderDetail` both
  // read from here so a fallback-verified customer gets the SAME
  // "select another order without re-verifying" behavior a reused
  // proof gets.
  const proofRef = useRef(hasReusableProof ? verifiedProof : null);
  // Restores scroll position when returning from detail -> list.
  const listScrollRef = useRef(null);
  const savedScrollTopRef = useRef(0);

  function activeProofBody() {
    const p = proofRef.current;
    if (!p) return null;
    return {
      catalog_id: catalogId,
      checkout_attempt_token: p.token,
      destination_channel: p.channel,
      destination_value: p.destinationValue,
    };
  }

  // UX correction (Aug 2026), §C.4 — a REUSED proof (from the parent's
  // earlier verification) that turns out to be expired/invalid gets the
  // dedicated "your verification has expired, please verify again"
  // explanation, since the customer has no reason to suspect anything
  // just went wrong. A proof this component JUST obtained itself (the
  // customer entered a phone number and completed an OTP moments ago,
  // right here) gets the ordinary, generic error message instead —
  // that's a genuine anomaly worth a plain retry prompt, not a claim
  // that "verification expired" seconds after succeeding.
  const proofIsReusedRef = useRef(hasReusableProof);

  async function fetchOrders(tokens) {
    // Called either directly (reused proof) or as VerificationModal's
    // onVerified callback (fallback flow) — tokens is undefined in the
    // first case.
    if (tokens) {
      const channel = "sms";
      const verified = tokens?.[channel];
      if (!verified?.token) {
        setStep("enter_phone");
        setErrorMsg("Verification did not complete. Please try again.");
        return;
      }
      proofRef.current = { channel, token: verified.token, destinationValue: phone };
      proofIsReusedRef.current = false;
      // Loyalty checkout no-request fix (Sep 2026) — hand this proof
      // upward.
      //
      // For Dine-In/Takeout this OTP is the ONLY real verification the
      // storefront offers (the welcome step's own auto-OTP and "Verify"
      // affordance are both scoped out of walk-in mode by the Product
      // Owner's walk-in decision). Until now the proof it produces
      // lived and died in this component's private ref, so a walk-in
      // member who genuinely verified still reached checkout with
      // nothing — the loyalty panel had no token and issued no request.
      //
      // This lifts a proof the customer ALREADY completed; it never
      // asks for a second one, and it is the mirror of the
      // `verifiedProof` prop above, which reuses a proof handed DOWN
      // for exactly the same reason.
      onLoyaltyProof?.({ channel, token: verified.token, destinationValue: phone });
    }
    setStep("loading");
    setExpiredNotice("");
    const body = activeProofBody();
    try {
      const { data } = await axios.post(`${API_BASE}/public/retail/${slug}/orders`, body);
      setOrders(data?.orders || []);
      setStep("list");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 401 && proofIsReusedRef.current) {
        // An expired/invalid REUSED grant never fails silently:
        // explain why, then offer a fresh OTP flow rather than a dead end.
        proofRef.current = null;
        setExpiredNotice("Your verification has expired. Please verify your phone number again to see your saved details and past orders.");
        setStep("enter_phone");
        return;
      }
      setErrorMsg(
        typeof detail === "string" && detail.trim()
          ? detail
          : "We couldn't retrieve your orders right now. Please try again in a moment.",
      );
      setStep("enter_phone");
    }
  }

  // Reused-proof path: fetch immediately on mount, no phone entry, no
  // OTP dialog. Runs once — `verifiedProof` is only ever set by the
  // parent after a genuine verification, never mutated in place.
  useEffect(() => {
    if (hasReusableProof) {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function openOrderDetail(orderCode) {
    savedScrollTopRef.current = listScrollRef.current?.scrollTop || 0;
    setDetailError("");
    setDetailLoading(true);
    setStep("detail");
    const body = activeProofBody();
    try {
      const { data } = await axios.post(`${API_BASE}/public/retail/${slug}/orders/${orderCode}`, body);
      setSelectedOrder(data?.order || null);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401) {
        proofRef.current = null;
        setExpiredNotice("Your verification has expired. Please verify your phone number again to see your saved details and past orders.");
        setStep("enter_phone");
        setDetailLoading(false);
        return;
      }
      setDetailError("We couldn't load this order right now. Please try again in a moment.");
    }
    setDetailLoading(false);
  }

  function backToList() {
    setSelectedOrder(null);
    setDetailError("");
    setStep("list");
    // Restore scroll position after the list re-renders.
    requestAnimationFrame(() => {
      if (listScrollRef.current) listScrollRef.current.scrollTop = savedScrollTopRef.current;
    });
  }

  if (step === "verifying") {
    return (
      <VerificationModal
        slug={slug}
        catalogId={catalogId}
        sessionId={sessionId}
        customer={{ phone, smsConsent: initialSmsConsent }}
        requiredChannels={["sms"]}
        orderType="lookup"
        purpose="order_lookup"
        phoneAccessAllowed={phoneAccessAllowed}
        onClose={() => setStep("enter_phone")}
        onVerified={fetchOrders}
      />
    );
  }

  return (
    <StorefrontModal
      testId="past-orders-lookup-modal"
      title="Your orders"
      description="Look up your past orders"
      onCancel={onClose}
    >
      {/* Close-control correction (Sep 2026). Three real defects in the
          row this replaces, all of which made the only explicit
          dismissal hard or impossible to use:
            * `text-stone-400` on white is roughly 2.8:1 — below the 3:1
              minimum for a non-text control, so the × read as
              decoration rather than a button;
            * the hit area was the 20px glyph itself, well under the
              44px a thumb needs on the tablet this storefront runs on;
            * the row scrolled away with the content. This card is the
              scroll container (`overflow-y-auto` on the
              `StorefrontModal` panel), so on a long order history the
              header — and with it the only Close button — scrolled off
              the top and the customer was left with no visible way out.
          Now: an outlined, high-contrast control with a 44px target, in
          a header pinned to the top of the scrolling panel. Escape still
          closes (Radix, via `StorefrontModal`), and backdrop click
          deliberately still does NOT — so this control is never the only
          path, and never a missing one. */}
      <div className="sticky -top-6 -mx-6 -mt-6 mb-1 px-6 pt-6 pb-3 bg-white z-10 flex items-start justify-between gap-3">
        {step === "detail" ? (
          <button
            type="button"
            onClick={backToList}
            data-testid="past-orders-back-to-list"
            className="flex items-center gap-1 text-sm font-semibold text-orange-700 hover:text-orange-900"
          >
            <ChevronLeft className="w-4 h-4" /> Back to order list
          </button>
        ) : (
          <h3 className="font-serif text-xl text-stone-900">Your orders</h3>
        )}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close past orders"
          data-testid="past-orders-close"
          className="shrink-0 -mr-1 inline-flex items-center justify-center h-11 w-11 rounded-full border border-stone-300 text-stone-700 hover:bg-stone-100 hover:text-stone-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
        >
          <X className="w-5 h-5" aria-hidden="true" />
        </button>
      </div>

      {step === "enter_phone" && (
        <div className="mt-4 space-y-3" data-testid="past-orders-enter-phone">
          {expiredNotice && (
            <p className="text-sm text-amber-700" data-testid="past-orders-expired-notice">{expiredNotice}</p>
          )}
          <p className="text-sm text-stone-600">
            {phoneAccessAllowed
              ? "Enter the phone number you used to order to see your recent orders."
              : "Enter the phone number you used to order. We'll text you a verification code before showing your orders."}
          </p>
          <input
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(formatUsPhoneDisplay(e.target.value))}
            placeholder="Phone number"
            data-testid="past-orders-phone-input"
            className="w-full border border-stone-300 rounded-lg px-3 py-2 text-sm"
          />
          {/* Phone/OTP validation correction (Aug 2026) — this lookup
              previously gated "Send verification code" on `!phone.trim()`
              only, so any non-empty, incomplete number could open the
              OTP flow. Now uses the SAME strict `isValidPhone` gate
              WelcomeModal's Sales Order form already uses, so a customer
              can never reach `VerificationModal` (which performs no
              validation of its own) with an invalid number. */}
          {phone.trim() && !isValidPhone(phone) && (
            <p className="mt-1 text-xs text-rose-600" role="alert" data-testid="past-orders-phone-hint">
              Enter a complete 10-digit mobile number.
            </p>
          )}
          {errorMsg && (
            <p className="text-sm text-red-600" data-testid="past-orders-error">{errorMsg}</p>
          )}
          <button
            type="button"
            disabled={!isValidPhone(phone)}
            onClick={() => { if (!isValidPhone(phone)) return; setErrorMsg(""); setStep("verifying"); }}
            data-testid="past-orders-send-code"
            className="w-full px-4 py-2.5 rounded-full bg-orange-500 text-white text-sm font-semibold disabled:opacity-40 hover:bg-orange-600"
          >
            {phoneAccessAllowed ? "Continue" : "Send verification code"}
          </button>
        </div>
      )}

      {step === "loading" && (
        <div className="mt-6 text-center" data-testid="past-orders-loading">
          <Loader2 className="w-8 h-8 mx-auto text-orange-400 animate-spin" />
          <p className="mt-2 text-sm text-stone-500">Loading your orders…</p>
        </div>
      )}

      {step === "list" && (
        <div
          ref={listScrollRef}
          className="mt-4 space-y-3 max-h-[60vh] overflow-y-auto"
          data-testid="past-orders-list"
        >
          {orders.length === 0 && (
            <p className="text-sm text-stone-500" data-testid="past-orders-empty">
              We couldn't find any past orders for this phone number.
            </p>
          )}
          {orders.map((o) => (
            <button
              type="button"
              key={o.order_code}
              onClick={() => openOrderDetail(o.order_code)}
              data-testid={`past-order-${o.order_code}`}
              className="block w-full text-left border border-stone-200 rounded-xl px-4 py-3 hover:border-orange-300 hover:bg-orange-50/50"
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-stone-900 flex items-center gap-2">
                  <PackageSearch className="w-4 h-4 text-orange-500" />
                  {o.order_code}
                </span>
                <span className="text-sm text-stone-500">
                  {o.placed_at ? new Date(o.placed_at).toLocaleDateString() : ""}
                </span>
              </div>
              <div className="mt-1 text-sm text-stone-600">
                {o.fulfillment_mode ? `${o.fulfillment_mode} · ` : ""}
                {typeof o.total === "number" ? `${o.currency?.toUpperCase() || "USD"} ${o.total.toFixed(2)}` : ""}
              </div>
              {/* Truthful refund status — mirrors the SAME wording
                  `RetailPaymentReturn.jsx` uses, never a blanket
                  "cancelled" or "completed" claim. */}
              {o.fully_refunded && (
                <div className="mt-1 text-sm text-amber-700" data-testid={`past-order-${o.order_code}-refunded`}>
                  Fully refunded
                  {o.same_order_recovery_eligible ? " · you can still retry delivery for this order" : ""}
                </div>
              )}
              {o.partially_refunded && (
                <div className="mt-1 text-sm text-amber-700">Partially refunded</div>
              )}
            </button>
          ))}
        </div>
      )}

      {step === "detail" && (
        <div className="mt-4" data-testid="past-order-detail">
          {detailLoading && (
            <div className="text-center py-6" data-testid="past-order-detail-loading">
              <Loader2 className="w-8 h-8 mx-auto text-orange-400 animate-spin" />
            </div>
          )}
          {!detailLoading && detailError && (
            <p className="text-sm text-red-600" data-testid="past-order-detail-error">{detailError}</p>
          )}
          {!detailLoading && !detailError && selectedOrder && (
            <div className="space-y-3">
              <div>
                <div className="flex items-center gap-2 text-lg font-semibold text-stone-900" data-testid="past-order-detail-code">
                  <PackageSearch className="w-5 h-5 text-orange-500" />
                  {selectedOrder.order_code}
                </div>
                <div className="text-sm text-stone-500">
                  {selectedOrder.placed_at ? new Date(selectedOrder.placed_at).toLocaleString() : ""}
                </div>
                <div className="text-sm text-stone-700">
                  {selectedOrder.fulfillment_mode || ""}
                  {selectedOrder.payment_status_label ? ` · ${selectedOrder.payment_status_label}` : ""}
                </div>
              </div>

              {/* Post-deployment correction (Aug 2026) — the itemized
                  breakdown, read from the SAME authoritative persisted
                  snapshot `retail/orders.py::order_to_response` already
                  exposes on the standalone order-status route (never
                  recalculated from the current catalog — a product's
                  name/price may have changed since this order was
                  placed). Strictly read-only display: no button, link,
                  or input anywhere in this block. */}
              {Array.isArray(selectedOrder.items) && selectedOrder.items.length > 0 && (
                <div data-testid="past-order-detail-items" className="border border-stone-200 rounded-lg divide-y divide-stone-100">
                  {selectedOrder.items.map((it, i) => (
                    <div
                      key={`${it.product_name || "item"}-${i}`}
                      data-testid={`past-order-detail-item-${i}`}
                      className="px-3 py-2 flex items-start justify-between gap-3 text-sm"
                    >
                      <div className="flex-1 min-w-0 text-stone-800">
                        {it.quantity}× {it.product_name}
                      </div>
                      <div className="text-right text-stone-700 whitespace-nowrap">
                        <div>{formatMoney(it.line_total, selectedOrder.currency)}</div>
                        <div className="text-[11px] text-stone-500">@ {formatMoney(it.unit_price, selectedOrder.currency)}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="space-y-1 text-sm" data-testid="past-order-detail-totals">
                <TotalRow label="Subtotal" value={formatMoney(selectedOrder.subtotal, selectedOrder.currency)} />
                {Number(selectedOrder.discount) > 0 && (
                  <TotalRow label="Discount" value={`− ${formatMoney(selectedOrder.discount, selectedOrder.currency)}`} />
                )}
                <TotalRow label="Tax" value={formatMoney(selectedOrder.tax, selectedOrder.currency)} />
                {Number(selectedOrder.delivery_fee) > 0 && (
                  <TotalRow label="Delivery fee" value={formatMoney(selectedOrder.delivery_fee, selectedOrder.currency)} />
                )}
                {Number(selectedOrder.tip) > 0 && (
                  <TotalRow label="Tip" value={formatMoney(selectedOrder.tip, selectedOrder.currency)} />
                )}
                <TotalRow
                  label="Total"
                  value={typeof selectedOrder.total === "number" ? formatMoney(selectedOrder.total, selectedOrder.currency) : ""}
                  bold
                />
              </div>

              {selectedOrder.fully_refunded && (
                <div className="text-sm text-amber-700" data-testid="past-order-detail-refunded">
                  Fully refunded
                  {typeof selectedOrder.refunded_amount_cents === "number"
                    ? ` · ${formatMoney(selectedOrder.refunded_amount_cents / 100, selectedOrder.currency)} refunded`
                    : ""}
                  {selectedOrder.same_order_recovery_eligible ? " · you can still retry delivery for this order" : ""}
                </div>
              )}
              {selectedOrder.partially_refunded && (
                <div className="text-sm text-amber-700" data-testid="past-order-detail-partially-refunded">
                  Partially refunded
                  {typeof selectedOrder.refunded_amount_cents === "number"
                    ? ` · ${formatMoney(selectedOrder.refunded_amount_cents / 100, selectedOrder.currency)} refunded`
                    : ""}
                </div>
              )}
              {/* Read-only — this view never alters payment, refund,
                  delivery, or fulfillment state, and never creates a
                  new order. Any future "reorder" action is explicitly
                  out of scope for this correction. */}
            </div>
          )}
        </div>
      )}
    </StorefrontModal>
  );
}
